import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User, Truck, MaintenanceEntry, Part } from '../types';
import { DataService } from '../services/DataService';
import authService from '../services/AuthService';
import { notificationService } from '../services/NotificationService';
import { teamWorkspaceService } from '../services/TeamWorkspaceService';

interface AppState {
	currentUser: User | null;
	trucks: Truck[];
	maintenance: MaintenanceEntry[];
	parts: Part[];
	syncing: boolean;
	lastSyncTime: Date | null;
	tempTruckForm: Partial<Truck> | null;
	tempMaintenanceForm: Partial<MaintenanceEntry> | null;
	tempPartForm: Partial<Part> | null;
}

type AppAction = 
	| { type: 'SET_USER'; payload: User | null }
	| { type: 'SET_TRUCKS'; payload: Truck[] }
	| { type: 'ADD_TRUCK'; payload: Truck }
	| { type: 'UPDATE_TRUCK'; payload: { id: string; data: Partial<Truck> } }
	| { type: 'DELETE_TRUCK'; payload: string }
	| { type: 'SET_MAINTENANCE'; payload: MaintenanceEntry[] }
	| { type: 'ADD_MAINTENANCE'; payload: MaintenanceEntry }
	| { type: 'UPDATE_MAINTENANCE'; payload: { id: string; data: Partial<MaintenanceEntry> } }
	| { type: 'DELETE_MAINTENANCE'; payload: string }
	| { type: 'SET_PARTS'; payload: Part[] }
	| { type: 'ADD_PART'; payload: Part }
	| { type: 'UPDATE_PART'; payload: { id: string; data: Partial<Part> } }
	| { type: 'DELETE_PART'; payload: string }
	| { type: 'SET_SYNCING'; payload: boolean }
	| { type: 'SET_LAST_SYNC_TIME'; payload: Date }
	| { type: 'SET_TEMP_TRUCK_FORM'; payload: Partial<Truck> | null }
	| { type: 'SET_TEMP_MAINTENANCE_FORM'; payload: Partial<MaintenanceEntry> | null }
	| { type: 'SET_TEMP_PART_FORM'; payload: Partial<Part> | null }
	| { type: 'CLEAR_ALL_DATA' };

const readStorageArray = <T,>(key: string): T[] => {
	try {
		const saved = localStorage.getItem(key);
		return saved ? JSON.parse(saved) : [];
	} catch (error) {
		console.error(`Error parsing ${key}:`, error);
		return [];
	}
};

const getUserDataScope = (user: User | null): string | null => {
	if (!user) return null;
	return user.companyId && user.companyId !== 'default-company' ? user.companyId : user.id;
};

const truckStatuses: Truck['status'][] = ['In Service', 'Out for Repair', 'Needs Attention', 'Retired'];

const parseDateValue = (value: any, fallback = new Date()): Date => {
	if (!value) return fallback;
	if (value instanceof Date) return Number.isNaN(value.getTime()) ? fallback : value;
	if (typeof value?.toDate === 'function') {
		const date = value.toDate();
		return Number.isNaN(date.getTime()) ? fallback : date;
	}
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? fallback : date;
};

const parseNumberValue = (value: any, fallback = 0): number => {
	if (typeof value === 'number' && Number.isFinite(value)) return value;
	const parsed = Number(String(value ?? '').replace(/[$,%]/g, '').replace(/,/g, '').trim());
	return Number.isFinite(parsed) ? parsed : fallback;
};

const sanitizeTruck = (truck: any, index = 0): Truck => {
	const now = new Date();
	const status = truckStatuses.includes(truck?.status) ? truck.status : 'In Service';

	return {
		id: String(truck?.id || `truck-${Date.now()}-${index}`),
		vin: String(truck?.vin || '').trim(),
		licensePlate: String(truck?.licensePlate || truck?.plate || '').trim(),
		make: String(truck?.make || '').trim(),
		model: String(truck?.model || '').trim(),
		year: Math.trunc(parseNumberValue(truck?.year, new Date().getFullYear())),
		mileage: Math.max(0, Math.trunc(parseNumberValue(truck?.mileage, 0))),
		nickname: truck?.nickname ? String(truck.nickname) : '',
		status,
		customFields: truck?.customFields && typeof truck.customFields === 'object' ? truck.customFields : {},
		createdAt: parseDateValue(truck?.createdAt, now),
		updatedAt: parseDateValue(truck?.updatedAt, now),
		createdBy: String(truck?.createdBy || 'unknown'),
		updatedBy: String(truck?.updatedBy || truck?.createdBy || 'unknown')
	};
};

const sanitizeMaintenance = (entry: any, index = 0): MaintenanceEntry => {
	const now = new Date();
	return {
		id: String(entry?.id || `maintenance-${Date.now()}-${index}`),
		truckId: String(entry?.truckId || ''),
		type: entry?.type || 'General Repair',
		date: parseDateValue(entry?.date, now),
		mileage: Math.max(0, Math.trunc(parseNumberValue(entry?.mileage, 0))),
		cost: Math.max(0, parseNumberValue(entry?.cost, 0)),
		notes: entry?.notes ? String(entry.notes) : '',
		images: Array.isArray(entry?.images) ? entry.images : undefined,
		performedBy: entry?.performedBy ? String(entry.performedBy) : '',
		invoiceNumber: entry?.invoiceNumber ? String(entry.invoiceNumber) : '',
		warrantyExpiration: entry?.warrantyExpiration ? parseDateValue(entry.warrantyExpiration) : undefined,
		status: entry?.status ? String(entry.status) : '',
		customFields: entry?.customFields && typeof entry.customFields === 'object' ? entry.customFields : {},
		createdAt: parseDateValue(entry?.createdAt, now),
		createdBy: String(entry?.createdBy || 'unknown'),
		updatedAt: parseDateValue(entry?.updatedAt, now),
		updatedBy: String(entry?.updatedBy || entry?.createdBy || 'unknown')
	};
};

const sanitizePart = (part: any, index = 0): Part => {
	const now = new Date();
	return {
		id: String(part?.id || `part-${Date.now()}-${index}`),
		name: String(part?.name || '').trim(),
		partNumber: String(part?.partNumber || part?.sku || '').trim(),
		category: part?.category || 'Other',
		cost: Math.max(0, parseNumberValue(part?.cost, 0)),
		supplier: part?.supplier ? String(part.supplier) : '',
		supplierPartNumber: part?.supplierPartNumber ? String(part.supplierPartNumber) : '',
		inventoryLevel: Math.max(0, Math.trunc(parseNumberValue(part?.inventoryLevel ?? part?.quantity, 0))),
		minStockLevel: Math.max(0, Math.trunc(parseNumberValue(part?.minStockLevel ?? part?.minQuantity, 0))),
		location: part?.location ? String(part.location) : '',
		customFields: part?.customFields && typeof part.customFields === 'object' ? part.customFields : {},
		createdAt: parseDateValue(part?.createdAt, now),
		createdBy: String(part?.createdBy || 'unknown'),
		updatedAt: parseDateValue(part?.updatedAt, now),
		updatedBy: String(part?.updatedBy || part?.createdBy || 'unknown')
	};
};

const sanitizeStateCollections = (state: Partial<AppState>) => ({
	trucks: Array.isArray(state.trucks) ? state.trucks.map(sanitizeTruck) : [],
	maintenance: Array.isArray(state.maintenance) ? state.maintenance.map(sanitizeMaintenance) : [],
	parts: Array.isArray(state.parts) ? state.parts.map(sanitizePart) : []
});

const getLegacySnapshotRecordCount = (legacyLocalStorage: Record<string, string> | undefined): number => {
	if (!legacyLocalStorage) return 0;
	return Object.entries(legacyLocalStorage).reduce((count, [key, value]) => {
		if (!/(downtimeRecords|calendarEvents|maintenanceTemplates|maintenanceSchedules|user_data_)/.test(key)) {
			return count;
		}

		try {
			const parsed = JSON.parse(value);
			if (Array.isArray(parsed)) return count + parsed.length;
			if (parsed && typeof parsed === 'object') {
				return count + Object.values(parsed as Record<string, unknown>).reduce((innerCount: number, entry: unknown) => (
					Array.isArray(entry) ? innerCount + entry.length : innerCount
				), 0);
			}
		} catch {
			return count;
		}

		return count;
	}, 0);
};

const getSnapshotRecordCount = (snapshot: any): number => (
	(snapshot?.appState?.trucks?.length || 0) +
	(snapshot?.appState?.maintenance?.length || 0) +
	(snapshot?.appState?.parts?.length || 0) +
	(snapshot?.fleetOpsWorkspace?.inspections?.length || 0) +
	(snapshot?.fleetOpsWorkspace?.issueReports?.length || 0) +
	(snapshot?.fleetOpsWorkspace?.workOrders?.length || 0) +
	(snapshot?.fleetOpsWorkspace?.complianceDocuments?.length || 0) +
	(snapshot?.fleetOpsWorkspace?.telematicsEvents?.length || 0) +
	(snapshot?.fleetOpsWorkspace?.teamMembers?.length || 0) +
	getLegacySnapshotRecordCount(snapshot?.legacyLocalStorage)
);

// Function to get user-specific localStorage state
const getUserSpecificState = (userId: string | null): AppState => {
	if (!userId || typeof window === 'undefined') {
		return {
			currentUser: null,
			trucks: [],
			maintenance: [],
			parts: [],
			syncing: false,
			lastSyncTime: new Date(),
			tempTruckForm: null,
			tempMaintenanceForm: null,
			tempPartForm: null,
		};
	}

	const userStateKey = `fleetfix_app_state_${userId}`;
	const persistedState = localStorage.getItem(userStateKey);
	
	if (persistedState) {
		try {
			const parsedState = JSON.parse(persistedState);
			const sanitizedCollections = sanitizeStateCollections(parsedState);
			return {
				...parsedState,
				currentUser: null,
				...sanitizedCollections,
				syncing: false,
				lastSyncTime: parsedState.lastSyncTime ? new Date(parsedState.lastSyncTime) : new Date(),
			};
		} catch (error) {
			console.error('Error parsing persisted state:', error);
		}
	}

	const legacyTrucks = readStorageArray<Truck>(`fleetfix_trucks_${userId}`).map(sanitizeTruck);
	const legacyMaintenance = readStorageArray<MaintenanceEntry>(`fleetfix_maintenance_${userId}`).map(sanitizeMaintenance);
	const legacyParts = readStorageArray<Part>(`fleetfix_parts_${userId}`).map(sanitizePart);

	return {
		currentUser: null, // Will be set when user logs in
		trucks: legacyTrucks,
		maintenance: legacyMaintenance,
		parts: legacyParts,
		syncing: false,
		lastSyncTime: new Date(),
		tempTruckForm: null,
		tempMaintenanceForm: null,
		tempPartForm: null,
	};
};

const initialState: AppState = getUserSpecificState(null);

function appReducer(state: AppState, action: AppAction): AppState {
	let newState: AppState;
	switch (action.type) {
		case 'SET_USER':
			// When user changes, load their specific state or clear all data if logout
			if (action.payload) {
				// User logged in - load their specific state
				const userSpecificState = getUserSpecificState(getUserDataScope(action.payload));
				newState = { ...userSpecificState, currentUser: action.payload };
			} else {
				// User logged out - clear all data
				newState = {
					currentUser: null,
					trucks: [],
					maintenance: [],
					parts: [],
					syncing: false,
					lastSyncTime: new Date(),
					tempTruckForm: null,
					tempMaintenanceForm: null,
					tempPartForm: null,
				};
			}
			break;
		case 'SET_TRUCKS':
			newState = { ...state, trucks: action.payload.map(sanitizeTruck) };
			break;
		case 'ADD_TRUCK':
			newState = { ...state, trucks: [sanitizeTruck(action.payload), ...state.trucks] };
			break;
		case 'UPDATE_TRUCK':
			newState = {
				...state,
				trucks: state.trucks.map(truck =>
					truck.id === action.payload.id
						? sanitizeTruck({ ...truck, ...action.payload.data })
						: truck
				),
			};
			break;
		case 'DELETE_TRUCK':
			newState = {
				...state,
				trucks: state.trucks.filter(truck => truck.id !== action.payload),
			};
			break;
		case 'SET_MAINTENANCE':
			newState = { ...state, maintenance: action.payload.map(sanitizeMaintenance) };
			break;
		case 'ADD_MAINTENANCE':
			newState = { ...state, maintenance: [sanitizeMaintenance(action.payload), ...state.maintenance] };
			break;
		case 'UPDATE_MAINTENANCE':
			newState = {
				...state,
				maintenance: state.maintenance.map(item =>
					item.id === action.payload.id
						? sanitizeMaintenance({ ...item, ...action.payload.data })
						: item
				),
			};
			break;
		case 'DELETE_MAINTENANCE':
			newState = {
				...state,
				maintenance: state.maintenance.filter(item => item.id !== action.payload),
			};
			break;
		case 'SET_PARTS':
			newState = { ...state, parts: action.payload.map(sanitizePart) };
			break;
		case 'ADD_PART':
			newState = { ...state, parts: [sanitizePart(action.payload), ...state.parts] };
			break;
		case 'UPDATE_PART':
			newState = {
				...state,
				parts: state.parts.map(part =>
					part.id === action.payload.id
						? sanitizePart({ ...part, ...action.payload.data })
						: part
				),
			};
			break;
		case 'DELETE_PART':
			newState = {
				...state,
				parts: state.parts.filter(part => part.id !== action.payload),
			};
			break;
		case 'SET_SYNCING':
			newState = { ...state, syncing: action.payload };
			break;
		case 'SET_LAST_SYNC_TIME':
			newState = { ...state, lastSyncTime: action.payload };
			break;
		case 'SET_TEMP_TRUCK_FORM':
			newState = { ...state, tempTruckForm: action.payload };
			break;
		case 'SET_TEMP_MAINTENANCE_FORM':
			newState = { ...state, tempMaintenanceForm: action.payload };
			break;
		case 'SET_TEMP_PART_FORM':
			newState = { ...state, tempPartForm: action.payload };
			break;
		case 'CLEAR_ALL_DATA':
			newState = {
				currentUser: state.currentUser, // Keep current user but clear their data
				trucks: [],
				maintenance: [],
				parts: [],
				syncing: false,
				lastSyncTime: new Date(),
				tempTruckForm: null,
				tempMaintenanceForm: null,
				tempPartForm: null,
			};
			break;
	default:
		newState = state;
	}
	
	// Save state to user-specific localStorage key if user is logged in
	if (typeof window !== 'undefined' && newState.currentUser) {
		const userStateKey = `fleetfix_app_state_${getUserDataScope(newState.currentUser)}`;
		localStorage.setItem(userStateKey, JSON.stringify(newState));
	}
	return newState;
}

const AppContext = createContext<any>(undefined);

export const useAppContext = () => {
	const context = useContext(AppContext);
	if (context === undefined) {
		throw new Error('useAppContext must be used within an AppProvider');
	}
	return context;
};

interface AppProviderProps {
	children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
	const [state, dispatch] = useReducer(appReducer, initialState);
	const loadedWorkspaceScopeRef = React.useRef<string | null>(null);
	const cloudReadyScopeRef = React.useRef<string | null>(null);


	const dataService = React.useMemo(() => DataService.getInstance(), []);


	const addTruck = async (truck: Truck) => {
		await dataService.setCurrentUser(getUserDataScope(state.currentUser) || '');
		await dataService.createTruck(truck);
		dispatch({ type: 'ADD_TRUCK', payload: truck });
	};


	const updateTruck = async (id: string, updates: Partial<Truck>) => {
		await dataService.setCurrentUser(getUserDataScope(state.currentUser) || '');
		await dataService.updateTruck(id, updates);
		dispatch({ type: 'UPDATE_TRUCK', payload: { id, data: updates } });
	};

	const deleteTruck = async (id: string) => {
		await dataService.setCurrentUser(getUserDataScope(state.currentUser) || '');
		await dataService.deleteTruck(id);
		dispatch({ type: 'DELETE_TRUCK', payload: id });
	};


	const addPart = async (part: Part) => {
		await dataService.setCurrentUser(getUserDataScope(state.currentUser) || '');
		await dataService.createPart(part);
		dispatch({ type: 'ADD_PART', payload: part });
		
		// Check stock levels after adding a part
		setTimeout(() => {
			notificationService.checkPartsStock([...state.parts, part]);
		}, 100);
	};


	const updatePart = async (id: string, updates: Partial<Part>) => {
		await dataService.setCurrentUser(getUserDataScope(state.currentUser) || '');
		await dataService.updatePart(id, updates);
		dispatch({ type: 'UPDATE_PART', payload: { id, data: updates } });
		
		// Check stock levels after updating a part
		setTimeout(() => {
			const updatedParts = state.parts.map(part =>
				part.id === id ? { ...part, ...updates } : part
			);
			notificationService.checkPartsStock(updatedParts);
		}, 100);
	};

	const deletePart = async (id: string) => {
		await dataService.setCurrentUser(getUserDataScope(state.currentUser) || '');
		await dataService.deletePart(id);
		dispatch({ type: 'DELETE_PART', payload: id });
	};


	const addMaintenance = async (entry: MaintenanceEntry) => {
		await dataService.setCurrentUser(getUserDataScope(state.currentUser) || '');
		await dataService.createMaintenanceEntry(entry);
		dispatch({ type: 'ADD_MAINTENANCE', payload: entry });
	};


	const updateMaintenance = async (id: string, updates: Partial<MaintenanceEntry>) => {
		await dataService.setCurrentUser(getUserDataScope(state.currentUser) || '');
		await dataService.updateMaintenanceEntry(id, updates);
		dispatch({ type: 'UPDATE_MAINTENANCE', payload: { id, data: updates } });
	};

	const deleteMaintenance = async (id: string) => {
		await dataService.setCurrentUser(getUserDataScope(state.currentUser) || '');
		await dataService.deleteMaintenanceEntry(id);
		dispatch({ type: 'DELETE_MAINTENANCE', payload: id });
	};

	const saveTempTruckForm = (formData: Partial<Truck>) => {
		dispatch({ type: 'SET_TEMP_TRUCK_FORM', payload: formData });
	};

	const clearTempTruckForm = () => {
		dispatch({ type: 'SET_TEMP_TRUCK_FORM', payload: null });
	};

	useEffect(() => {
		const unsubscribers: (() => void)[] = [];
		const authUnsubscribe = authService.onAuthStateChange(async (user) => {
			notificationService.setUserContext(getUserDataScope(user));
			dispatch({ type: 'SET_USER', payload: user });
			
			// Initialize user data persistence when user logs in
			if (user) {
				try {
					await teamWorkspaceService.recordUser(user);
					// Import userDataService to initialize user data
					const userDataService = (await import('../services/UserDataService')).default;
					await userDataService.initializeUserData(getUserDataScope(user) || user.id);
					console.log('User data initialized successfully for:', getUserDataScope(user) || user.id);
				} catch (error) {
					console.error('Error initializing user data:', error);
				}
			}
		});
		unsubscribers.push(authUnsubscribe);
		return () => {
			unsubscribers.forEach(unsub => unsub());
		};
	}, []);

	useEffect(() => {
		if (!state.currentUser) {
			loadedWorkspaceScopeRef.current = null;
			cloudReadyScopeRef.current = null;
			return;
		}
		const scopeId = getUserDataScope(state.currentUser);
		if (!scopeId) return;
		if (loadedWorkspaceScopeRef.current === scopeId) return;
		loadedWorkspaceScopeRef.current = scopeId;
		cloudReadyScopeRef.current = null;

		let cancelled = false;
		const loadSharedWorkspace = async () => {
			const localWorkspaceState = {
				trucks: state.trucks,
				maintenance: state.maintenance,
				parts: state.parts
			};
			const localRecordCount = teamWorkspaceService.getLocalWorkspaceRecordCount(scopeId, localWorkspaceState);
			const snapshot = await teamWorkspaceService.loadSnapshot(scopeId);
			if (cancelled) return;

			const remoteRecordCount = getSnapshotRecordCount(snapshot);
			if (!snapshot || (remoteRecordCount === 0 && localRecordCount > 0)) {
				cloudReadyScopeRef.current = scopeId;
				if (localRecordCount > 0 && state.currentUser) {
					void teamWorkspaceService.publishSnapshot(state.currentUser, localWorkspaceState);
				}
				return;
			}

			cloudReadyScopeRef.current = scopeId;
			teamWorkspaceService.restoreSnapshotLocally(scopeId, snapshot);
			dispatch({ type: 'SET_TRUCKS', payload: snapshot.appState?.trucks || [] });
			dispatch({ type: 'SET_MAINTENANCE', payload: snapshot.appState?.maintenance || [] });
			dispatch({ type: 'SET_PARTS', payload: snapshot.appState?.parts || [] });
		};

		void loadSharedWorkspace();
		return () => {
			cancelled = true;
		};
	}, [state.currentUser, state.trucks, state.maintenance, state.parts]);

	useEffect(() => {
		if (!state.currentUser) return;
		const scopeId = getUserDataScope(state.currentUser);
		if (!scopeId || cloudReadyScopeRef.current !== scopeId) return;
		const timeout = window.setTimeout(() => {
			void teamWorkspaceService.publishSnapshot(state.currentUser!, {
				trucks: state.trucks,
				maintenance: state.maintenance,
				parts: state.parts
			});
		}, 700);

		return () => window.clearTimeout(timeout);
	}, [state.currentUser, state.trucks, state.maintenance, state.parts]);

	useEffect(() => {
		if (!state.currentUser) return;

		const publishCurrentWorkspace = () => {
			const scopeId = getUserDataScope(state.currentUser);
			if (!scopeId || cloudReadyScopeRef.current !== scopeId) return;
			void teamWorkspaceService.publishSnapshot(state.currentUser!, {
				trucks: state.trucks,
				maintenance: state.maintenance,
				parts: state.parts
			});
		};

		const warmup = window.setTimeout(publishCurrentWorkspace, 2500);
		const interval = window.setInterval(publishCurrentWorkspace, 15000);
		return () => {
			window.clearTimeout(warmup);
			window.clearInterval(interval);
		};
	}, [state.currentUser, state.trucks, state.maintenance, state.parts]);

	// Initialize and monitor stock levels
	useEffect(() => {
		if (state.parts && state.parts.length > 0) {
			// Check stock levels whenever parts data changes
			notificationService.checkPartsStock(state.parts);
		}
	}, [state.parts]);

	return (
		<AppContext.Provider value={{ 
			state, 
			dispatch, 
			addTruck, 
			updateTruck, 
			deleteTruck, 
			addPart, 
			updatePart, 
			deletePart, 
			addMaintenance, 
			updateMaintenance, 
			deleteMaintenance,
			saveTempTruckForm,
			clearTempTruckForm
		}}>
			{children}
		</AppContext.Provider>
	);
};

export default AppProvider;
